import { h, Component, createRef, JSX, RefObject, Fragment } from 'preact';
import { slideHide, slideShow } from '../utilities/animations.ts';

import {
	INPUT_MAX_LENGTH_INDICATOR_THRESHOLD,
	TEXTAREA_MAX_LENGTH_INDICATOR_THRESHOLD,
} from '../constants/input';

type InputProps = {
	value: string;
	name?: string;
	label?: string;
	placeholder?: string;
	validate?: boolean;
	showValidation?: boolean;
	validationMessage?: string | null;
	autoFocus?: boolean;
	autoSelect?: boolean;
	required?: boolean;
	maxLength?: number;
	maxLineBreaks?: number;
	autocomplete?: string;
	rows?: number;
	fieldRef?: RefObject<HTMLTextAreaElement | HTMLInputElement>;
	as?: 'input' | 'textarea';
	onInput?: (value: string, name?: string) => void;
	onFocus?: () => void;
	onBlur?: () => void;
	onKeyDown?: (
		event: JSX.TargetedKeyboardEvent<HTMLTextAreaElement | HTMLInputElement>
	) => void;
	onKeyUp?: (
		event: JSX.TargetedKeyboardEvent<HTMLTextAreaElement | HTMLInputElement>
	) => void;
	style?: JSX.CSSProperties;
	v2?: boolean;
};

type InputState = {
	isFocused: boolean;
	wasChanged: boolean;
	showValidationStatus: boolean;
};

type MismatchKey = `${HTMLInputElement['type']}Mismatch`;

export default class Input extends Component<InputProps, InputState> {
	baseClassName = 'input';
	focusedClassName = 'input--is-focused';
	contentClassName = 'input--has-content';
	fieldClassName = 'input__field';
	fieldNoInputLabelClassName = 'input__field--no-input-label';
	labelClassName = 'input__label';
	metaClassName = 'input__meta';
	validationBaseClassName = 'input__validation';
	validationErrorClassName = 'input__validation--error';
	showValidationStatusClassName = 'u-show-validation';
	hasValidation = false;
	maxLengthIndicatorThresholds = {
		input: INPUT_MAX_LENGTH_INDICATOR_THRESHOLD,
		textarea: TEXTAREA_MAX_LENGTH_INDICATOR_THRESHOLD,
	};

	input = createRef<HTMLInputElement>();
	textarea = createRef<HTMLTextAreaElement>();
	validationMessage = createRef<HTMLSpanElement>();

	errorMessages: Record<string, string> = {
		valueMissing: 'Vänligen fyll i fältet.',
		emailMismatch: 'Ange en giltig e-postadress.',
		patternMismatch: 'Värdet är inte rätt formaterat.',
	};

	constructor(props: InputProps) {
		super(props);

		if (props.v2) {
			this.baseClassName = 'v2-form__input-wrapper';
			this.focusedClassName = 'is-focused';
			this.contentClassName = 'has-content';
			this.fieldClassName = 'v2-form__input';
			this.fieldNoInputLabelClassName = 'v2-form__input--no-input-label';
			this.labelClassName = 'v2-form__label';
			this.metaClassName = 'v2-form__meta';
			this.validationBaseClassName = 'v2-form__validation';
			this.validationErrorClassName = 'v2-form__validation--error';
			this.showValidationStatusClassName = 'u-show-validation';
		}

		this.state = {
			isFocused: false,
			wasChanged: this.props.value?.length > 0,
			showValidationStatus: this.props.value?.length > 0,
		};
	}

	componentDidUpdate(prevProps: InputProps) {
		// If the value changes externally, without onChange firing on the field,
		// we still need to show validation status.
		if (this.props.value !== prevProps.value) {
			this.setState({ wasChanged: true });
		}

		if (this.props.validationMessage !== prevProps.validationMessage) {
			const el = this.input.current || this.textarea.current;

			if (el) {
				el.setCustomValidity(this.props.validationMessage || '');

				el.reportValidity();
			}
		}

		if (this.validationMessage.current) {
			if (this.props.validationMessage && !prevProps.validationMessage) {
				slideShow(this.validationMessage.current);
			} else if (!this.props.validationMessage && prevProps.validationMessage) {
				slideHide(this.validationMessage.current);
			}
		}
	}

	componentDidMount(): void {
		if (this.props.autoFocus) {
			this.input.current?.focus();
			this.textarea.current?.focus();
		}

		if (this.props.autoSelect) {
			this.input.current?.select();
			this.textarea.current?.select();
		}
	}

	render() {
		const {
			label,
			maxLength,
			fieldRef,
			as: asElement = 'input',
			...props
		} = this.props;

		const maxLengthIndicatorThreshold =
			this.maxLengthIndicatorThresholds[asElement] || 0;

		let classNames = this.baseClassName;
		if (this.state.isFocused) {
			classNames += ' ' + this.focusedClassName;
		}
		if (this.props.value?.length > 0) {
			classNames += ' ' + this.contentClassName;
		}

		const hasPlaceholder =
			typeof this.props.placeholder === 'string' &&
			this.props.placeholder.length > 0;

		let inputClassNames = this.fieldClassName;
		if (this.state.showValidationStatus || this.props.showValidation) {
			inputClassNames += ' ' + this.showValidationStatusClassName;
		}

		if (hasPlaceholder) {
			inputClassNames += ` ${this.fieldNoInputLabelClassName}`;
		}

		return (
			<Fragment>
				{hasPlaceholder ? (
					<p
						className="heading heading--no-underline"
						style={{ marginBottom: 0 }}
					>
						{this.props.label}
					</p>
				) : null}
				<label className={classNames} style={this.props.style}>
					{!hasPlaceholder && (
						<span className={this.labelClassName}>{label}</span>
					)}
					{asElement === 'input' && (
						<input
							className={inputClassNames}
							{...props}
							onInput={this.handleInput}
							onFocus={this.handleFocus}
							onBlur={this.handleBlur}
							onKeyUp={this.props.onKeyUp}
							onKeyDown={this.handleKeyDown}
							onInvalid={this.handleInvalid}
							required={this.props.required}
							maxLength={maxLength}
							autocomplete={this.props.autocomplete}
							ref={(el) => {
								this.input.current = el;
								if (fieldRef) {
									fieldRef.current = el;
								}
							}}
						/>
					)}
					{asElement === 'textarea' && (
						<textarea
							className={inputClassNames}
							{...props}
							onInput={this.handleInput}
							onFocus={this.handleFocus}
							onBlur={this.handleBlur}
							onKeyUp={this.props.onKeyUp}
							onKeyDown={this.handleKeyDown}
							onInvalid={this.handleInvalid}
							required={this.props.required}
							maxLength={maxLength}
							ref={(el) => {
								this.textarea.current = el;
								if (fieldRef) {
									fieldRef.current = el;
								}
							}}
						/>
					)}
					{maxLength &&
						this.props.value.length >
							maxLength * maxLengthIndicatorThreshold && (
							<span className={this.metaClassName}>
								{this.props.value.length || 0} av max {maxLength} tecken
							</span>
						)}
					<span
						className={[
							this.validationBaseClassName,
							this.validationErrorClassName,
						].join(' ')}
						aria-live="polite"
						ref={this.validationMessage}
					>
						{this.props.validationMessage && this.props.validationMessage}
					</span>
				</label>
			</Fragment>
		);
	}

	handleInput = (
		ev: JSX.TargetedEvent<HTMLInputElement | HTMLTextAreaElement>
	) => {
		this.setState({ wasChanged: true });
		if (
			!(
				ev.target instanceof HTMLInputElement ||
				ev.target instanceof HTMLTextAreaElement
			)
		) {
			return;
		}

		// Pasted or dropped text bypasses the keydown line break check, and
		// maxlength isn't enforced consistently across browsers, so clamp here.
		const field = ev.target;
		const value = this.limitValue(field.value);
		if (value !== field.value) {
			const caret = Math.min(
				field.selectionStart ?? value.length,
				value.length
			);
			field.value = value;
			field.setSelectionRange(caret, caret);
		}

		if (typeof this.props.onInput === 'function') {
			this.props.onInput(value, this.props.name);
		}
	};

	limitValue = (value: string) => {
		const { maxLength, maxLineBreaks } = this.props;

		if (maxLineBreaks) {
			// Mirror handleKeyDown: no consecutive empty lines, and line breaks
			// beyond the limit are turned into spaces.
			let linebreaks = 0;
			value = value
				.replace(/\r\n?/g, '\n')
				.replace(/\n{3,}/g, '\n\n')
				.replace(/\n/g, () => (++linebreaks > maxLineBreaks ? ' ' : '\n'));
		}

		if (maxLength && value.length > maxLength) {
			value = value.slice(0, maxLength);
		}

		return value;
	};

	handleKeyDown = (
		ev: JSX.TargetedKeyboardEvent<HTMLTextAreaElement | HTMLInputElement>
	) => {
		if (this.props.onKeyDown) {
			this.props.onKeyDown(ev);
		}

		if (this.props.maxLineBreaks) {
			const linebreaks = (this.props.value.match(/\n/g) || '').length + 1;
			const endsInDoubleLinebreak = this.props.value.match(/\n{2,}$/);
			if (
				ev.key === 'Enter' &&
				(linebreaks > this.props.maxLineBreaks || endsInDoubleLinebreak)
			) {
				ev.preventDefault();
			}
		}
	};

	handleFocus = () => {
		this.setState({ isFocused: true });
		if (typeof this.props.onFocus === 'function') {
			this.props.onFocus();
		}
	};

	handleBlur = () => {
		this.setState((prevState) => ({
			isFocused: false,
			showValidationStatus: prevState.wasChanged,
		}));

		if (typeof this.props.onBlur === 'function') {
			this.props.onBlur();
		}
	};

	handleInvalid = (e: Event) => {
		// Prevent the browser's default validation UI from showing
		e.preventDefault();
	};
}
